import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAnalytics, isSupported, logEvent as firebaseLogEvent, type Analytics } from 'firebase/analytics';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const hasFirebaseConfig = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);

// Resolves to null when Firebase isn't configured yet, or when the browser
// doesn't support the Analytics SDK (private browsing, some ad-blockers) —
// every call site awaits this and no-ops on null rather than crashing.
const analyticsPromise: Promise<Analytics | null> = hasFirebaseConfig
  ? isSupported()
      .then((supported) => {
        const app: FirebaseApp = initializeApp(firebaseConfig);
        return supported ? getAnalytics(app) : null;
      })
      .catch(() => null)
  : Promise.resolve(null);

if (!hasFirebaseConfig) {
  console.warn(
    '[firebase] Analytics not configured — missing VITE_FIREBASE_* environment variables. Page views and events will no-op.',
  );
}

export async function logPageView(path: string) {
  const analytics = await analyticsPromise;
  if (!analytics) return;
  firebaseLogEvent(analytics, 'page_view', { page_path: path, page_location: window.location.href });
}

export async function logAnalyticsEvent(name: string, params?: Record<string, unknown>) {
  const analytics = await analyticsPromise;
  if (!analytics) return;
  firebaseLogEvent(analytics, name, params);
}
